import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../contexts/GameContext';
import { t } from '../i18n/translations';
import { 
  getSettings, updateSettings, getLeads, deleteLead, exportLeadsCSV,
  getScores, deleteScore, resetScores, getAssets, uploadAsset, deleteAsset, resetFair,
  uploadBanner, uploadEmailImage, getGameSprites, uploadGameSprite, resetGameSprite
} from '../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from './ui/table';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle 
} from './ui/dialog';
import { 
  Settings, Music, Users, Trophy, RotateCcw, 
  ArrowLeft, Save, Trash2, Download, Upload, Play, Pause, X, Grid3X3, Image,
  Palette, Gamepad2, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import LevelEditor from './LevelEditor';

// ========================
// SPRITES MANAGER COMPONENT
// ========================
const SpritesManager = ({ gameSprites, setGameSprites, spriteUploading, setSpriteUploading }) => {
  const [ver, setVer] = useState(Date.now());
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!gameSprites) {
      getGameSprites().then(setGameSprites).catch(() => toast.error('Errore caricamento sprites'));
    }
  }, [gameSprites, setGameSprites]);

  const getSpritePreviewUrl = (sprite) => {
    if (sprite.is_custom) {
      return `${backendUrl}/api/game-sprites/${sprite.key}/image?v=${ver}`;
    }
    return sprite.default_url;
  };

  const handleUpload = async (spriteKey, file) => {
    setSpriteUploading(spriteKey);
    try {
      await uploadGameSprite(spriteKey, file);
      const updated = await getGameSprites();
      setGameSprites(updated);
      setVer(Date.now());
      toast.success('Sprite aggiornato!');
    } catch {
      toast.error('Errore upload sprite');
    } finally {
      setSpriteUploading(null);
    }
  };

  const handleReset = async (spriteKey) => {
    setSpriteUploading(spriteKey);
    try {
      await resetGameSprite(spriteKey);
      const updated = await getGameSprites();
      setGameSprites(updated);
      setVer(Date.now());
      toast.success('Sprite ripristinato!');
    } catch {
      toast.error('Errore reset sprite');
    } finally {
      setSpriteUploading(null);
    }
  };

  if (!gameSprites) {
    return <div className="text-center text-gray-500 py-8">Caricamento sprites...</div>;
  }

  const categoryOrder = ['characters', 'game_elements', 'road_tiles', 'wall_tiles'];

  return (
    <div className="space-y-8">
      {categoryOrder.map(catKey => {
        const cat = gameSprites[catKey];
        if (!cat) return null;
        return (
          <div key={catKey} className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">{cat.label}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {cat.items.map(sprite => (
                <div 
                  key={sprite.key} 
                  className={`relative border-2 rounded-lg p-3 flex flex-col items-center gap-2 transition-colors ${
                    sprite.is_custom ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                  data-testid={`sprite-card-${sprite.key}`}
                >
                  {sprite.is_custom && (
                    <span className="absolute top-1 right-1 text-[10px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                      CUSTOM
                    </span>
                  )}
                  <div className="w-16 h-16 flex items-center justify-center bg-[#04082B] rounded border border-gray-300">
                    <img
                      src={getSpritePreviewUrl(sprite)}
                      alt={sprite.label}
                      className="max-w-full max-h-full object-contain"
                      style={{ imageRendering: 'pixelated' }}
                    />
                  </div>
                  <span className="text-xs text-gray-700 text-center font-medium leading-tight">{sprite.label}</span>
                  <div className="flex gap-1 w-full">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/png"
                        className="hidden"
                        onChange={e => {
                          if (e.target.files[0]) handleUpload(sprite.key, e.target.files[0]);
                          e.target.value = '';
                        }}
                        disabled={spriteUploading === sprite.key}
                      />
                      <span className={`flex items-center justify-center gap-1 text-xs py-1.5 px-2 rounded w-full transition-colors ${
                        spriteUploading === sprite.key
                          ? 'bg-gray-300 text-gray-500 cursor-wait'
                          : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                      }`}>
                        <Upload className="w-3 h-3" />
                        {spriteUploading === sprite.key ? '...' : 'Carica'}
                      </span>
                    </label>
                    {sprite.is_custom && (
                      <button
                        onClick={() => handleReset(sprite.key)}
                        disabled={spriteUploading === sprite.key}
                        className="text-xs py-1.5 px-2 rounded bg-red-100 text-red-600 hover:bg-red-200 transition-colors disabled:opacity-50"
                        data-testid={`sprite-reset-${sprite.key}`}
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ========================
// ADMIN PANEL COMPONENT
// ========================
const AdminPanel = () => {
  const { language, navigateTo } = useGame();
  const [activeTab, setActiveTab] = useState('aspetto');
  const [settings, setSettings] = useState(null);
  const [leads, setLeads] = useState([]);
  const [scores, setScores] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Dialog states
  const [deleteLeadDialog, setDeleteLeadDialog] = useState({ open: false, id: null });
  const [deleteScoreDialog, setDeleteScoreDialog] = useState({ open: false, id: null });
  const [resetScoresDialog, setResetScoresDialog] = useState(false);
  const [resetFairDialog, setResetFairDialog] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  
  // Audio preview
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = React.useRef(null);
  
  // Game sprites
  const [gameSprites, setGameSprites] = useState(null);
  const [spriteUploading, setSpriteUploading] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [settingsData, leadsData, scoresData, assetsData] = await Promise.all([
        getSettings(),
        getLeads(),
        getScores(100),
        getAssets()
      ]);
      setSettings(settingsData);
      setLeads(leadsData);
      setScores(scoresData);
      setAssets(assetsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Impostazioni salvate!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleDeleteLead = async () => {
    try {
      await deleteLead(deleteLeadDialog.id);
      setLeads(prev => prev.filter(l => l.id !== deleteLeadDialog.id));
      toast.success('Lead eliminato');
    } catch (error) {
      toast.error('Errore eliminazione lead');
    } finally {
      setDeleteLeadDialog({ open: false, id: null });
    }
  };

  const handleDeleteScore = async () => {
    try {
      await deleteScore(deleteScoreDialog.id);
      setScores(prev => prev.filter(s => s.id !== deleteScoreDialog.id));
      toast.success('Punteggio eliminato');
    } catch (error) {
      toast.error('Errore eliminazione punteggio');
    } finally {
      setDeleteScoreDialog({ open: false, id: null });
    }
  };

  const handleResetScores = async () => {
    try {
      await resetScores();
      setScores([]);
      toast.success('Classifica resettata');
    } catch (error) {
      toast.error('Errore reset classifica');
    } finally {
      setResetScoresDialog(false);
    }
  };

  const handleResetFair = async () => {
    if (resetConfirmation !== 'RESET') {
      toast.error('Digita RESET per confermare');
      return;
    }
    try {
      await resetFair(resetConfirmation);
      setLeads([]);
      setScores([]);
      setAssets([]);
      toast.success('Reset fiera completato!');
    } catch (error) {
      toast.error('Errore reset fiera');
    } finally {
      setResetFairDialog(false);
      setResetConfirmation('');
    }
  };

  const handleAssetUpload = async (assetId, name, file) => {
    try {
      await uploadAsset(assetId, file, name);
      await loadData();
      toast.success(`${name} caricato!`);
    } catch (error) {
      toast.error('Errore caricamento asset');
    }
  };

  const handlePlayAudio = (asset) => {
    if (playingAudio === asset.id) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`data:${asset.mime_type};base64,${asset.data}`);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => setPlayingAudio(null);
      setPlayingAudio(asset.id);
    }
  };

  const getAssetData = (id) => assets.find(a => a.id === id);

  if (isLoading || !settings) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#f8fafc] overflow-y-auto" data-testid="admin-panel" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', zIndex: 40 }}>
      {/* Header */}
      <header className="bg-[#005EB8] text-white p-4 flex items-center gap-4 sticky top-0 z-10">
        <button
          onClick={() => navigateTo('attract')}
          className="p-2 hover:bg-white/10 rounded transition-colors"
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-lg">{t('admin.title', language)}</h1>
      </header>

      {/* Main Content */}
      <main className="p-4 max-w-6xl mx-auto pb-20">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1 w-full mb-6">
            <TabsTrigger value="aspetto" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              <span className="hidden sm:inline">Aspetto</span>
            </TabsTrigger>
            <TabsTrigger value="gioco" className="flex items-center gap-2">
              <Gamepad2 className="w-4 h-4" />
              <span className="hidden sm:inline">Gioco</span>
            </TabsTrigger>
            <TabsTrigger value="sprites" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Sprites</span>
            </TabsTrigger>
            <TabsTrigger value="editor" className="flex items-center gap-2">
              <Grid3X3 className="w-4 h-4" />
              <span className="hidden sm:inline">Livelli</span>
            </TabsTrigger>
            <TabsTrigger value="audio" className="flex items-center gap-2">
              <Music className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.audio', language)}</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.leads', language)}</span>
            </TabsTrigger>
            <TabsTrigger value="ranking" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.ranking', language)}</span>
            </TabsTrigger>
            <TabsTrigger value="reset" className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">Reset</span>
            </TabsTrigger>
          </TabsList>

          {/* ══════ ASPETTO ══════ */}
          <TabsContent value="aspetto" className="space-y-6">
            {/* Colori UI */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">{t('admin.colors', language)}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {[
                  { key: 'color_attract_bg', label: 'Sfondo Home' },
                  { key: 'color_game_bg', label: 'Sfondo Gioco' },
                  { key: 'color_btn_play', label: 'Pulsante Gioca' },
                  { key: 'color_leaderboard_header', label: 'Classifica Header' },
                  { key: 'color_text_accent', label: 'Testo Accento' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-sm text-gray-600 mb-1">{label}</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={settings[key]} onChange={(e) => handleSettingChange(key, e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                      <Input value={settings[key]} onChange={(e) => handleSettingChange(key, e.target.value)} className="flex-1" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Badge */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Badge</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Colore Testo</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings.badge_text_color || '#FFFFFF'} onChange={(e) => handleSettingChange('badge_text_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                    <Input value={settings.badge_text_color || '#FFFFFF'} onChange={(e) => handleSettingChange('badge_text_color', e.target.value)} className="flex-1" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Colore Sfondo</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings.badge_bg_color || '#000000'} onChange={(e) => handleSettingChange('badge_bg_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                    <Input value={settings.badge_bg_color || '#000000'} onChange={(e) => handleSettingChange('badge_bg_color', e.target.value)} className="flex-1" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Larghezza (px)</label>
                  <Input type="number" min="80" max="400" value={settings.badge_width || '200'} onChange={(e) => handleSettingChange('badge_width', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Altezza (px)</label>
                  <Input type="number" min="16" max="100" value={settings.badge_height || 'auto'} onChange={(e) => handleSettingChange('badge_height', e.target.value)} placeholder="auto" data-testid="badge-height-input" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Dim. Testo (px)</label>
                  <Input type="number" min="8" max="24" value={settings.badge_font_size || '12'} onChange={(e) => handleSettingChange('badge_font_size', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Trasparenza: {Math.round((settings.badge_opacity ?? 1) * 100)}%</label>
                  <input type="range" min="0" max="1" step="0.05" value={settings.badge_opacity ?? 1} onChange={(e) => handleSettingChange('badge_opacity', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#005EB8] mt-2" data-testid="badge-opacity-slider" />
                </div>
              </div>
              <div className="mt-3 p-3 bg-gray-100 rounded flex items-center justify-center" style={{ background: 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 16px 16px' }}>
                <div className="rounded-full px-3 py-1 font-medium flex items-center gap-1" style={{ backgroundColor: settings.badge_bg_color || '#000000', color: settings.badge_text_color || '#FFFFFF', width: `${settings.badge_width || 200}px`, height: settings.badge_height && settings.badge_height !== 'auto' ? `${settings.badge_height}px` : 'auto', fontSize: `${settings.badge_font_size || 12}px`, opacity: settings.badge_opacity ?? 1, justifyContent: 'center', alignItems: 'center' }}>
                  Made with Emergent
                </div>
              </div>
            </div>

            {/* Testi Form */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Testi Form</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.formTitleIt', language)}</label>
                  <Input value={settings.form_title_it} onChange={(e) => handleSettingChange('form_title_it', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.formTitleEn', language)}</label>
                  <Input value={settings.form_title_en} onChange={(e) => handleSettingChange('form_title_en', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Privacy Policy (GDPR)</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Testo Privacy IT</label>
                    <textarea
                      value={settings.privacy_text_it || ''}
                      onChange={(e) => handleSettingChange('privacy_text_it', e.target.value)}
                      className="w-full border rounded-md p-2 text-sm min-h-[80px] resize-y"
                      data-testid="input-privacy-text-it"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Testo Privacy EN</label>
                    <textarea
                      value={settings.privacy_text_en || ''}
                      onChange={(e) => handleSettingChange('privacy_text_en', e.target.value)}
                      className="w-full border rounded-md p-2 text-sm min-h-[80px] resize-y"
                      data-testid="input-privacy-text-en"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Etichetta Link IT</label>
                    <Input
                      value={settings.privacy_link_label_it || ''}
                      onChange={(e) => handleSettingChange('privacy_link_label_it', e.target.value)}
                      data-testid="input-privacy-link-label-it"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Etichetta Link EN</label>
                    <Input
                      value={settings.privacy_link_label_en || ''}
                      onChange={(e) => handleSettingChange('privacy_link_label_en', e.target.value)}
                      data-testid="input-privacy-link-label-en"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Link Privacy IT (URL)</label>
                    <Input
                      type="url"
                      value={settings.privacy_link_it || ''}
                      onChange={(e) => handleSettingChange('privacy_link_it', e.target.value)}
                      placeholder="https://www.example.com/privacy-policy-it"
                      data-testid="input-privacy-link-it"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Link Privacy EN (URL)</label>
                    <Input
                      type="url"
                      value={settings.privacy_link_en || ''}
                      onChange={(e) => handleSettingChange('privacy_link_en', e.target.value)}
                      placeholder="https://www.example.com/privacy-policy-en"
                      data-testid="input-privacy-link-en"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400">Se compilati, verrà aggiunto un link cliccabile sotto il testo della privacy (aperto in una nuova scheda).</p>
              </div>
            </div>

            {/* Schermata Iniziale */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Schermata Iniziale</h2>
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Testo "Tocca per Iniziare"</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Italiano</label>
                    <Input value={settings.tap_to_start_it || 'TOCCA PER INIZIARE'} onChange={(e) => handleSettingChange('tap_to_start_it', e.target.value)} data-testid="input-tap-start-it" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">English</label>
                    <Input value={settings.tap_to_start_en || 'TAP TO START'} onChange={(e) => handleSettingChange('tap_to_start_en', e.target.value)} data-testid="input-tap-start-en" />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Banner Home</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['it', 'en'].map(lang => (
                    <div key={lang} className="border rounded-lg p-4 bg-gray-50">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Banner {lang === 'it' ? 'Italiano' : 'English'}</label>
                      {settings[`banner_${lang}`] && (
                        <div className="mb-3 bg-[#04082B] rounded p-3 flex justify-center">
                          <img src={settings[`banner_${lang}`]} alt={`Banner ${lang}`} className="max-h-24 object-contain" data-testid={`banner-preview-${lang}`} />
                        </div>
                      )}
                      <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-2 bg-[#005EB8] text-white rounded text-sm hover:bg-[#004a93] transition-colors">
                        <Upload className="w-4 h-4" /> Carica Banner {lang.toUpperCase()}
                        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          try { const result = await uploadBanner(lang, file); handleSettingChange(`banner_${lang}`, result.src); toast.success(`Banner ${lang.toUpperCase()} caricato!`); }
                          catch { toast.error('Errore caricamento banner'); }
                        }} className="hidden" data-testid={`banner-upload-${lang}`} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Testi End Screen */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Testi End Screen</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.gameoverTextIt', language)}</label>
                    <textarea className="w-full p-2 border rounded resize-none h-24" value={settings.gameover_text_it} onChange={(e) => handleSettingChange('gameover_text_it', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.gameoverTextEn', language)}</label>
                    <textarea className="w-full p-2 border rounded resize-none h-24" value={settings.gameover_text_en} onChange={(e) => handleSettingChange('gameover_text_en', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.timeoutTextIt', language)}</label>
                    <textarea className="w-full p-2 border rounded resize-none h-24" value={settings.timeout_text_it} onChange={(e) => handleSettingChange('timeout_text_it', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">{t('admin.timeoutTextEn', language)}</label>
                    <textarea className="w-full p-2 border rounded resize-none h-24" value={settings.timeout_text_en} onChange={(e) => handleSettingChange('timeout_text_en', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleSaveSettings} disabled={isSaving} className="w-full bg-[#005EB8] hover:bg-[#004a93]" data-testid="save-aspetto-btn">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Salvataggio...' : 'Salva Aspetto'}
            </Button>
          </TabsContent>

          {/* ══════ GIOCO ══════ */}
          <TabsContent value="gioco" className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Impostazioni Gioco</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.gameDuration', language)}</label>
                  <Input type="number" value={settings.game_duration} onChange={(e) => handleSettingChange('game_duration', parseInt(e.target.value))} data-testid="input-game-duration" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.powerupDuration', language)}</label>
                  <Input type="number" value={settings.powerup_duration} onChange={(e) => handleSettingChange('powerup_duration', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.playerSpeed', language)}</label>
                  <Input type="number" value={settings.player_speed} onChange={(e) => handleSettingChange('player_speed', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Vite Giocatore</label>
                  <Input type="number" min={1} max={9} value={settings.player_lives ?? 3} onChange={(e) => handleSettingChange('player_lives', Math.max(1, Math.min(9, parseInt(e.target.value) || 1)))} data-testid="input-player-lives" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Bonus Frutta - Ogni (sec)</label>
                  <Input type="number" value={settings.bonus_interval} onChange={(e) => handleSettingChange('bonus_interval', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Bonus Frutta - Durata (sec)</label>
                  <Input type="number" value={settings.bonus_duration} onChange={(e) => handleSettingChange('bonus_duration', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Bonus Frutta - Punti</label>
                  <Input type="number" value={settings.bonus_points} onChange={(e) => handleSettingChange('bonus_points', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.inactivityTimeout', language)}</label>
                  <Input type="number" value={settings.inactivity_timeout} onChange={(e) => handleSettingChange('inactivity_timeout', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.endscreenTimeout', language)}</label>
                  <Input type="number" value={settings.endscreen_timeout} onChange={(e) => handleSettingChange('endscreen_timeout', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.adminPin', language)}</label>
                  <Input type="password" value={settings.admin_pin} onChange={(e) => handleSettingChange('admin_pin', e.target.value)} maxLength={4} />
                </div>
              </div>
            </div>

            {/* Fasce Punteggio */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Fasce Punteggio</h2>
              <div className="space-y-4">
                {[1, 2].map(tier => (
                  <div key={tier} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Soglia Fascia {tier}</label>
                      <Input type="number" value={settings[`tier${tier}_threshold`]} onChange={(e) => handleSettingChange(`tier${tier}_threshold`, parseInt(e.target.value))} />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Titolo IT</label>
                      <Input value={settings[`tier${tier}_title_it`]} onChange={(e) => handleSettingChange(`tier${tier}_title_it`, e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Titolo EN</label>
                      <Input value={settings[`tier${tier}_title_en`]} onChange={(e) => handleSettingChange(`tier${tier}_title_en`, e.target.value)} />
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Titolo Fascia 3 IT ({">"}{settings.tier2_threshold})</label>
                    <Input value={settings.tier3_title_it} onChange={(e) => handleSettingChange('tier3_title_it', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Titolo Fascia 3 EN</label>
                    <Input value={settings.tier3_title_en} onChange={(e) => handleSettingChange('tier3_title_en', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Opzioni Fiera */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Opzioni Fiera</h2>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mostra "Salta registrazione (test)"</label>
                  <p className="text-xs text-gray-400">Mostra il pulsante per saltare il form di registrazione e avviare direttamente il gioco.</p>
                </div>
                <button
                  onClick={() => handleSettingChange('show_skip_registration', !settings.show_skip_registration)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.show_skip_registration ? 'bg-[#005EB8]' : 'bg-gray-300'}`}
                  data-testid="toggle-skip-registration"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.show_skip_registration ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <Button onClick={handleSaveSettings} disabled={isSaving} className="w-full bg-[#005EB8] hover:bg-[#004a93]" data-testid="save-gioco-btn">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Salvataggio...' : 'Salva Gioco'}
            </Button>
          </TabsContent>

          {/* ══════ SPRITES ══════ */}
          <TabsContent value="sprites" className="space-y-6">
            <SpritesManager 
              gameSprites={gameSprites} 
              setGameSprites={setGameSprites}
              spriteUploading={spriteUploading}
              setSpriteUploading={setSpriteUploading}
            />
          </TabsContent>

          {/* ══════ LEVEL EDITOR ══════ */}
          <TabsContent value="editor">
            <div className="bg-white rounded-lg shadow p-4">
              <LevelEditor />
            </div>
          </TabsContent>

          {/* ══════ AUDIO ══════ */}
          <TabsContent value="audio">
            {/* Volume Controls */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Controllo Volume</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-gray-600">Volume Musica</label>
                    <span className="text-sm font-mono font-bold text-[#005EB8]" data-testid="volume-music-value">
                      {Math.round((settings.volume_music ?? 0.4) * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.volume_music ?? 0.4}
                    onChange={(e) => handleSettingChange('volume_music', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#005EB8]"
                    data-testid="volume-music-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>Gameplay, Power-up, Attract</span>
                    <span>100%</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-gray-600">Volume Effetti Sonori</label>
                    <span className="text-sm font-mono font-bold text-[#005EB8]" data-testid="volume-effects-value">
                      {Math.round((settings.volume_effects ?? 0.6) * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.volume_effects ?? 0.6}
                    onChange={(e) => handleSettingChange('volume_effects', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#005EB8]"
                    data-testid="volume-effects-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>Pillola, Nemico, Game Over</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="w-full mt-4 bg-[#005EB8] hover:bg-[#004a93]"
                data-testid="save-audio-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Salvataggio...' : 'Salva Volume'}
              </Button>
            </div>

            {/* Audio Assets */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">{t('admin.audio', language)}</h2>
              <div className="space-y-4">
                {[
                  { id: 'audio_gameplay', name: 'Musica Gameplay (Loop)' },
                  { id: 'audio_powerup', name: 'Musica Power-up (Loop)' },
                  { id: 'audio_dot', name: 'Effetto Pillola' },
                  { id: 'audio_powerup_collect', name: 'Effetto Power-up Raccolto' },
                  { id: 'audio_enemy_eat', name: 'Effetto Nemico Mangiato' },
                  { id: 'audio_gameover', name: 'Effetto Game Over' },
                  { id: 'audio_attract', name: 'Musica Attract Screen (Loop)' }
                ].map(audio => {
                  const asset = getAssetData(audio.id);
                  return (
                    <div key={audio.id} className="flex items-center gap-4 p-4 border rounded">
                      <div className="flex-1">
                        <p className="font-medium">{audio.name}</p>
                        <p className="text-sm text-gray-500">
                          {asset ? 'Caricato' : 'Non caricato'}
                        </p>
                      </div>
                      {asset && (
                        <button
                          onClick={() => handlePlayAudio(asset)}
                          className="p-2 rounded bg-gray-100 hover:bg-gray-200"
                        >
                          {playingAudio === audio.id ? (
                            <Pause className="w-5 h-5" />
                          ) : (
                            <Play className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      <label className="cursor-pointer">
                        <span className="px-3 py-2 bg-[#005EB8] text-white rounded text-sm hover:bg-[#004a93] flex items-center gap-1">
                          <Upload className="w-4 h-4" />
                          Carica
                        </span>
                        <input
                          type="file"
                          accept="audio/mp3,audio/ogg,audio/wav"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleAssetUpload(audio.id, audio.name, file);
                          }}
                          className="hidden"
                        />
                      </label>
                      {asset && (
                        <button
                          onClick={() => deleteAsset(audio.id).then(loadData)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* ══════ EMAIL ══════ */}
          <TabsContent value="email" className="space-y-6">
            {/* Toggle + Credenziali */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Configurazione Email</h2>
              <div className="mb-6 flex items-center gap-3">
                <label className="text-sm font-medium">Email Attiva</label>
                <button
                  onClick={() => handleSettingChange('email_enabled', !settings.email_enabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${settings.email_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  data-testid="email-enabled-toggle-tab"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.email_enabled ? 'translate-x-6' : ''}`} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">SMTP Host</label>
                  <Input value={settings.smtp_host || ''} onChange={(e) => handleSettingChange('smtp_host', e.target.value)} placeholder="smtp.office365.com" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">SMTP Port</label>
                  <Input type="number" value={settings.smtp_port || 587} onChange={(e) => handleSettingChange('smtp_port', parseInt(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">SMTP Username</label>
                  <Input value={settings.smtp_user || ''} onChange={(e) => handleSettingChange('smtp_user', e.target.value)} placeholder="user@domain.com" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">SMTP Password</label>
                  <Input type="password" value={settings.smtp_pass || ''} onChange={(e) => handleSettingChange('smtp_pass', e.target.value)} placeholder="••••••••" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">Email Mittente (From)</label>
                  <Input value={settings.smtp_from || ''} onChange={(e) => handleSettingChange('smtp_from', e.target.value)} placeholder="sales_italia@palletways.com" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">Nome Mittente</label>
                  <Input value={settings.smtp_from_name || ''} onChange={(e) => handleSettingChange('smtp_from_name', e.target.value)} placeholder="Palletways Italia" />
                </div>
              </div>
            </div>

            {/* Immagini Email */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Immagini Email</h2>
              {['header', 'footer'].map(section => (
                <div key={section} className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">{section}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[{ lang: 'it', label: 'Italiano' }, { lang: 'en', label: 'English' }].map(({ lang, label }) => {
                      const key = `${section}_${lang}`;
                      const field = `email_${key}_image`;
                      return (
                        <div key={key} className="border rounded-lg p-4 bg-gray-50">
                          <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>
                          {settings[field] && (
                            <div className="mb-2 bg-[#0F172A] rounded p-3 flex justify-center">
                              <img src={settings[field]} alt={key} className="max-h-16 object-contain" />
                            </div>
                          )}
                          <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 bg-[#005EB8] text-white rounded text-xs hover:bg-[#004a93]">
                            <Upload className="w-3 h-3" /> Carica {section} {label}
                            <input type="file" accept="image/png,image/jpeg" onChange={async (e) => {
                              const file = e.target.files?.[0]; if (!file) return;
                              try { const r = await uploadEmailImage(key, file); handleSettingChange(field, r.src); toast.success(`${section} ${label} caricato!`); }
                              catch { toast.error('Errore caricamento'); }
                            }} className="hidden" />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Template Vittoria */}
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
              <h2 className="text-lg font-semibold mb-4 text-green-700">Template Vittoria (Fascia 2-3)</h2>
              <div className="space-y-4">
                {['it', 'en'].map(lang => (
                  <div key={lang} className="space-y-2 p-3 bg-gray-50 rounded">
                    <label className="text-xs font-bold text-gray-500 uppercase">{lang === 'it' ? 'Italiano' : 'English'}</label>
                    <Input value={settings[`email_subject_good_${lang}`] || ''} onChange={e => handleSettingChange(`email_subject_good_${lang}`, e.target.value)} placeholder="Oggetto email" />
                    <textarea rows={3} value={settings[`email_body_good_${lang}`] || ''} onChange={e => handleSettingChange(`email_body_good_${lang}`, e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder="Corpo email..." />
                    <Input value={settings[`email_cta_good_${lang}`] || ''} onChange={e => handleSettingChange(`email_cta_good_${lang}`, e.target.value)} placeholder="Testo pulsante CTA" />
                  </div>
                ))}
              </div>
            </div>

            {/* Template Sconfitta */}
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
              <h2 className="text-lg font-semibold mb-4 text-red-700">Template Sconfitta (Fascia 1)</h2>
              <div className="space-y-4">
                {['it', 'en'].map(lang => (
                  <div key={lang} className="space-y-2 p-3 bg-gray-50 rounded">
                    <label className="text-xs font-bold text-gray-500 uppercase">{lang === 'it' ? 'Italiano' : 'English'}</label>
                    <Input value={settings[`email_subject_bad_${lang}`] || ''} onChange={e => handleSettingChange(`email_subject_bad_${lang}`, e.target.value)} placeholder="Oggetto email" />
                    <textarea rows={3} value={settings[`email_body_bad_${lang}`] || ''} onChange={e => handleSettingChange(`email_body_bad_${lang}`, e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder="Corpo email..." />
                    <Input value={settings[`email_cta_bad_${lang}`] || ''} onChange={e => handleSettingChange(`email_cta_bad_${lang}`, e.target.value)} placeholder="Testo pulsante CTA" />
                  </div>
                ))}
              </div>
            </div>

            {/* CTA URL + Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">URL & Info</h2>
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">URL Pulsante CTA</label>
                <Input value={settings.email_cta_url || ''} onChange={e => handleSettingChange('email_cta_url', e.target.value)} placeholder="https://www.palletways.com" />
              </div>
              <div className="text-xs text-gray-400 bg-gray-50 rounded p-3 space-y-1">
                <div><strong>Variabili:</strong> <code className="bg-gray-200 px-1 rounded">{'{nome}'}</code> <code className="bg-gray-200 px-1 rounded">{'{punteggio}'}</code> <code className="bg-gray-200 px-1 rounded">{'{titolo}'}</code></div>
                <div><strong>Logica invio:</strong> Fascia 1 = email sconfitta | Fascia 2-3 = email vittoria</div>
              </div>
              <div className="mt-4 flex gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/preview-email?language=it&is_good=true`} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700">Anteprima Vittoria</a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/preview-email?language=it&is_good=false`} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-700">Anteprima Sconfitta</a>
              </div>
            </div>

            <Button onClick={handleSaveSettings} disabled={isSaving} className="w-full bg-[#005EB8] hover:bg-[#004a93]" data-testid="save-email-btn">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Salvataggio...' : 'Salva Email'}
            </Button>
          </TabsContent>

          {/* ══════ LEADS ══════ */}
          <TabsContent value="leads">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-semibold">{t('admin.leads', language)} ({leads.length})</h2>
                <Button onClick={exportLeadsCSV} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Azienda</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefono</TableHead>
                      <TableHead>Città</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead, index) => (
                      <TableRow key={lead.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{lead.nome}</TableCell>
                        <TableCell>{lead.azienda}</TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.telefono}</TableCell>
                        <TableCell>{lead.citta}</TableCell>
                        <TableCell>{lead.score}</TableCell>
                        <TableCell>{new Date(lead.created_at).toLocaleString('it-IT')}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => setDeleteLeadDialog({ open: true, id: lead.id })}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {leads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-gray-500 py-8">
                          Nessun lead registrato
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ══════ CLASSIFICA ══════ */}
          <TabsContent value="ranking">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-semibold">{t('admin.ranking', language)} (Top {scores.length})</h2>
                <Button onClick={() => setResetScoresDialog(true)} variant="destructive">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset Classifica
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Iniziali</TableHead>
                      <TableHead>Punteggio</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scores.map((score, index) => (
                      <TableRow key={score.id}>
                        <TableCell className="font-bold text-[#FFD100]">{index + 1}</TableCell>
                        <TableCell className="font-mono font-bold">{score.initials}</TableCell>
                        <TableCell>{score.score.toLocaleString()}</TableCell>
                        <TableCell>{new Date(score.created_at).toLocaleString('it-IT')}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => setDeleteScoreDialog({ open: true, id: score.id })}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {scores.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          Nessun punteggio registrato
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ══════ RESET FIERA ══════ */}
          <TabsContent value="reset">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 text-red-600">{t('admin.reset', language)}</h2>
              <p className="text-gray-600 mb-6">
                Questa operazione cancellerà i lead registrati e la classifica.
                Tutte le altre impostazioni, sprite, audio e configurazioni rimarranno invariate.
              </p>
              <Button
                onClick={() => setResetFairDialog(true)}
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="reset-fair-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                RESET COMPLETO FIERA
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Lead Dialog */}
      <Dialog open={deleteLeadDialog.open} onOpenChange={(open) => !open && setDeleteLeadDialog({ open: false, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Lead</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo lead? L'operazione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteLeadDialog({ open: false, id: null })}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDeleteLead}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Score Dialog */}
      <Dialog open={deleteScoreDialog.open} onOpenChange={(open) => !open && setDeleteScoreDialog({ open: false, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Punteggio</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo punteggio dalla classifica?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteScoreDialog({ open: false, id: null })}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDeleteScore}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Scores Dialog */}
      <Dialog open={resetScoresDialog} onOpenChange={setResetScoresDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Classifica</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler cancellare tutta la classifica? L'operazione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetScoresDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleResetScores}>
              Reset Classifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Fair Dialog */}
      <Dialog open={resetFairDialog} onOpenChange={setResetFairDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Reset Completo Fiera</DialogTitle>
            <DialogDescription>
              Questa operazione cancellerà TUTTI i dati della fiera. Digita "RESET" per confermare.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={resetConfirmation}
            onChange={(e) => setResetConfirmation(e.target.value.toUpperCase())}
            placeholder="Digita RESET"
            className="my-4"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetFairDialog(false); setResetConfirmation(''); }}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleResetFair}
              disabled={resetConfirmation !== 'RESET'}
            >
              Conferma Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
